import { useCallback } from 'react';
import { forceEndAllSessions, VoicePreference } from '../../services/cxone-sdk.service';
import { resolveVoicePreferenceForConnection } from '../../utils/config';
import { logger } from '../../utils/logger';
import { CXoneState } from '../../types';
import { CXONE_CONNECTION_STEPS, getCxoneConnectionSteps } from './cxone-connection-steps';
import { CxoneSessionRefs, initialCxoneState } from './types';

interface UseCxoneForceLogoutOptions {
  refs: CxoneSessionRefs;
  initializeWithVoicePreference: (preference: VoicePreference) => Promise<void>;
  setState: React.Dispatch<React.SetStateAction<CXoneState>>;
  setHasSessionConflict: React.Dispatch<React.SetStateAction<boolean>>;
  setNeedsVoicePreference: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCxoneForceLogout({
  refs,
  initializeWithVoicePreference,
  setState,
  setHasSessionConflict,
  setNeedsVoicePreference,
}: UseCxoneForceLogoutOptions) {
  const forceLogoutAndReconnect = useCallback(async () => {
    logger.debug('[CXone] Force logout and reconnect requested...');

    const preference = resolveVoicePreferenceForConnection(refs.voicePreferenceRef.current);
    const steps = preference
      ? getCxoneConnectionSteps(preference, { includeEndingSession: true })
      : [CXONE_CONNECTION_STEPS.endingSession];

    setState((prev) => ({
      ...prev,
      isConnecting: true,
      connectionError: null,
      connectionProgress: {
        steps,
        activeStep: CXONE_CONNECTION_STEPS.endingSession,
      },
    }));

    try {
      await forceEndAllSessions();
      setHasSessionConflict(false);
      refs.initializedRef.current = false;
      refs.initializingRef.current = false;

      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (preference) {
        await initializeWithVoicePreference(preference);
      } else {
        setNeedsVoicePreference(true);
        setState(initialCxoneState);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to force logout';
      logger.error('[CXone] Force logout failed:', errorMessage);
      setState({
        isInitialized: false,
        isConnecting: false,
        isConnected: false,
        connectionError: errorMessage,
        connectionProgress: null,
      });
    }
  }, [
    refs,
    initializeWithVoicePreference,
    setHasSessionConflict,
    setNeedsVoicePreference,
    setState,
  ]);

  return { forceLogoutAndReconnect };
}
